// Récompenses du pass et objets sans palette : libellés et pictogrammes originaux, dessinés en SVG.
// Les objets de la saison 1 (emblèmes, bannières, effets…) n'ont pas de visuel en jeu : le pictogramme montre
// leur type, et les collections sont présentées en plan technique (silhouette au trait). À partir de la saison 2,
// un objet du pass peut être un vrai cosmétique (COSMETIC_DEF) : il est montré tel qu'il est rendu en jeu.
import type { CSSProperties } from 'react';
import { RAW_PASS } from '../../../shared/catalog/data.generated';
import { getClass } from '../../../shared/catalog/classes';
import { COSMETIC_DEF, DEFAULT_LOOK, RARITY_COLOR, type CosmeticDef, type CosmeticSlot, type PartPaint, type TankLook } from '../../../shared/cosmetics';
import { css } from '../game/colors';
import { t, type Key } from '../i18n';
import { passItemLabel } from '../i18n/catalog';
import { CoinIcon, GemIcon, TokenIcon } from './icons';
import { cosName } from './PaintPage';
import { SwatchImage, TankImage } from './TankPreview';
import { BLUEPRINT } from './tankArt';

export type Reward =
  | { kind: 'coins'; amount: number }
  | { kind: 'gems'; amount: number }
  | { kind: 'token'; token: string; amount: number }
  | { kind: 'item'; item: string; label: string };

export type PassLevel = { level: number; free: Reward; premium: Reward };

export type ItemKind = 'emblem' | 'banner' | 'killfx' | 'deathfx' | 'shotfx' | 'trail' | 'frame' | 'collection' | 'aura' | 'item';

/** Cosmétique réel donné par une récompense d'objet (saison 2 et suivantes), ou null (objet S1, monnaie, jeton). */
export function cosmeticOf(r: Reward): CosmeticDef | null {
  return r.kind === 'item' ? (COSMETIC_DEF[r.item] ?? null) : null;
}

// ─── Tableau de la saison 1, lu dans le catalogue (mêmes règles que server/src/accounts/pass.ts) ─────
// Sert à nommer les objets S1 pendant les saisons suivantes, sans requête, et aux planches de démonstration.
function slug(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function parsePassText(text: string): Reward {
  const s = text.trim();
  let m = /^(\d+) pièces$/.exec(s);
  if (m) return { kind: 'coins', amount: Number(m[1]) };
  m = /^(\d+) gemmes$/.exec(s);
  if (m) return { kind: 'gems', amount: Number(m[1]) };
  if (s.startsWith('Jeton')) {
    const count = /×(\d+)$/.exec(s) && !/^Jeton ×\d$/.test(s) ? Number(/×(\d+)$/.exec(s)![1]) : 1;
    let token = 'tok_x2';
    if (s.startsWith('Jeton ×5')) token = 'tok_x5';
    else if (s.startsWith('Jeton ×2')) token = 'tok_x2';
    else if (s.includes('restauration')) token = 'tok_restore';
    else if (s.includes('réinitialisation')) token = 'tok_reset';
    else if (s.includes('salon')) token = 'tok_salon';
    return { kind: 'token', token, amount: count };
  }
  const label = s.replace(/ — premium$/, '');
  return { kind: 'item', item: `s1:${slug(label)}`, label };
}

let s1Levels: PassLevel[] | null = null;
/** Tableau des 105 niveaux de la saison 1 « GENÈSE MÉCANIQUE » (figé). */
export function s1PassLevels(): PassLevel[] {
  return (s1Levels ??= RAW_PASS.map((p) => ({ level: p.level, free: parsePassText(p.free), premium: parsePassText(p.premium) })));
}

let s1Labels: Map<string, string> | null = null;
/** Nom français d'un objet du pass de la saison 1 (`s1:*`), ou undefined. */
export function s1ItemLabel(id: string): string | undefined {
  if (!s1Labels) {
    s1Labels = new Map();
    for (const l of s1PassLevels()) for (const r of [l.free, l.premium]) if (r.kind === 'item') s1Labels.set(r.item, r.label);
  }
  return s1Labels.get(id);
}

/** Type d'un objet de saison, lu dans son identifiant (s1:embleme-…, s1:effet-mort-…). */
export function itemKind(item: string): ItemKind {
  const s = item.replace(/^[a-z0-9]+:/, '');
  if (s.startsWith('embleme')) return 'emblem';
  if (s.startsWith('banniere')) return 'banner';
  if (s.startsWith('effet-elimination')) return 'killfx';
  if (s.startsWith('effet-mort')) return 'deathfx';
  if (s.startsWith('effet-tir')) return 'shotfx';
  if (s.startsWith('trainee')) return 'trail';
  if (s.startsWith('cadre')) return 'frame';
  if (s.startsWith('collection')) return 'collection';
  return 'item';
}

export function rewardLabel(r: Reward): string {
  if (r.kind === 'coins') return t('rewardCoins', { n: r.amount });
  if (r.kind === 'gems') return t('rewardGems', { n: r.amount });
  if (r.kind === 'token') return `${t(`token_${r.token}` as Key)}${r.amount > 1 ? ` ×${r.amount}` : ''}`;
  if (COSMETIC_DEF[r.item]) return cosName(r.item);
  return passItemLabel(r.item, r.label);
}

export function rewardKindLabel(r: Reward): string {
  if (r.kind === 'coins') return t('rkind_coins');
  if (r.kind === 'gems') return t('rkind_gems');
  if (r.kind === 'token') return t('rkind_token');
  const d = COSMETIC_DEF[r.item];
  if (d) return slotLabel(d.slot);
  return t(`rkind_${itemKind(r.item)}` as Key);
}

/** Type d'un cosmétique au singulier (matière, motif, autocollant) ou nom de l'emplacement d'effet. */
export function slotLabel(slot: CosmeticSlot): string {
  if (slot === 'mat') return t('paint_material');
  if (slot === 'pat') return t('paint_pattern');
  if (slot === 'dec') return t('paint_decal');
  return t(`paint_slot_${slot}` as Key);
}

/** Récompense « marquante » : objet, jeton ou gemmes (les pièces courantes restent discrètes sur la piste). */
export const isMajor = (r: Reward) => r.kind === 'item';

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Teintes des pictogrammes, tirées de l'identifiant : stables d'une visite à l'autre. */
const TINTS = ['#f2a23a', '#93a6ba', '#c8794a', '#7cc7a2', '#dcbb62', '#c98fb0'];
const BLUEPRINT_CLASSES = ['C01-F', 'C02-A', 'C04-B', 'C06-A', 'C09-B', 'C13-A'];

function ItemGlyph({ kind, tint, seed }: { kind: ItemKind; tint: string; seed: number }) {
  const stroke = { fill: 'none', stroke: tint, strokeWidth: 2.4, strokeLinecap: 'square' as const, strokeLinejoin: 'miter' as const };
  switch (kind) {
    case 'emblem': {
      const inner = seed % 3;
      return (
        <g>
          <path d="M24 5l16 6v12c0 9.5-6.8 16.6-16 20-9.2-3.4-16-10.5-16-20V11z" fill="#1d2127" stroke={tint} strokeWidth={2.4} />
          {inner === 0 && <path d="M24 14l2.8 6.4 7 .6-5.3 4.6 1.6 6.8L24 29l-6.1 3.4 1.6-6.8-5.3-4.6 7-.6z" fill={tint} />}
          {inner === 1 && (
            <>
              <circle cx="24" cy="23" r="7" {...stroke} />
              <circle cx="24" cy="23" r="2.6" fill={tint} />
            </>
          )}
          {inner === 2 && <path d="M24 13v20M15 18l18 10M33 18L15 28" {...stroke} />}
        </g>
      );
    }
    case 'banner':
      return (
        <g>
          <path d="M12 5v38" stroke="#7d8085" strokeWidth={2.4} />
          <path d="M14 7h22v26l-11-6-11 6z" fill="#1d2127" stroke={tint} strokeWidth={2.4} strokeLinejoin="miter" />
          <path d="M14 13h22" stroke={tint} strokeWidth={4} />
          <circle cx="25" cy="21" r="3.4" fill={tint} />
        </g>
      );
    case 'frame':
      return (
        <g>
          <rect x="7" y="7" width="34" height="34" fill="#1d2127" stroke={tint} strokeWidth={2.4} />
          <rect x="13" y="13" width="22" height="22" fill="none" stroke={tint} strokeWidth={1.4} opacity=".6" />
          {[
            [10, 10],
            [38, 10],
            [10, 38],
            [38, 38],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="2.4" fill={tint} />
          ))}
        </g>
      );
    case 'trail':
      return (
        <g>
          <path d="M6 30h20M10 23h18M14 16h14" {...stroke} opacity=".55" />
          <path d="M6 30h20" {...stroke} />
          <circle cx="34" cy="23" r="7" fill="#1d2127" stroke={tint} strokeWidth={2.4} />
        </g>
      );
    case 'killfx':
      return (
        <g>
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2 + (seed % 7) * 0.1;
            return <path key={i} d={`M${24 + Math.cos(a) * 7} ${24 + Math.sin(a) * 7}L${24 + Math.cos(a) * 18} ${24 + Math.sin(a) * 18}`} {...stroke} />;
          })}
          <circle cx="24" cy="24" r="4" fill={tint} />
        </g>
      );
    case 'deathfx':
      return (
        <g>
          <path d="M24 10l6 10-6 4-6-4z" fill={tint} />
          <path d="M12 26l9 1-3 9-8-4z" fill={tint} opacity=".8" />
          <path d="M36 26l-9 1 3 9 8-4z" fill={tint} opacity=".65" />
          <path d="M22 30h4l1 8h-6z" fill={tint} opacity=".5" />
        </g>
      );
    case 'shotfx':
      return (
        <g>
          <rect x="5" y="20" width="16" height="8" fill="#2a2f36" stroke="#7d8085" strokeWidth={2} />
          <path d="M22 24l10-9 2 6 9 3-9 3-2 6z" fill={tint} />
          <circle cx="40" cy="24" r="3" fill={tint} />
        </g>
      );
    case 'aura':
      return (
        <g>
          <circle cx="24" cy="24" r="17" fill="none" stroke={tint} strokeWidth={1.4} strokeDasharray="3 4" opacity=".7" />
          <circle cx="24" cy="24" r="9" fill="#1d2127" stroke="#7d8085" strokeWidth={2.2} />
          <circle cx="24" cy="24" r="3.4" fill={tint} opacity=".55" />
          {Array.from({ length: 6 }, (_, i) => {
            const a = (i / 6) * Math.PI * 2 + (seed % 5) * 0.2;
            return <circle key={i} cx={24 + Math.cos(a) * 15} cy={24 + Math.sin(a) * 15} r={i % 2 ? 1.8 : 2.8} fill={tint} />;
          })}
        </g>
      );
    default:
      return <rect x="10" y="10" width="28" height="28" fill="#1d2127" stroke={tint} strokeWidth={2.4} />;
  }
}

/** Pictogramme d'une récompense, à la taille demandée (pixels CSS). */
export function RewardArt({ r, size = 48 }: { r: Reward; size?: number }) {
  if (r.kind === 'coins') {
    const n = r.amount >= 1000 ? 3 : r.amount >= 300 ? 2 : 1;
    return (
      <span className="reward-art reward-coins" style={{ width: size, height: size }} aria-hidden>
        {Array.from({ length: n }, (_, i) => (
          <span key={i} className="reward-stack" style={{ transform: `translate(${(i - (n - 1) / 2) * size * 0.2}px, ${-i * size * 0.08}px)` }}>
            <CoinIcon size={size * 0.62} />
          </span>
        ))}
      </span>
    );
  }
  if (r.kind === 'gems') {
    const n = r.amount >= 500 ? 3 : r.amount >= 100 ? 2 : 1;
    return (
      <span className="reward-art reward-gems" style={{ width: size, height: size }} aria-hidden>
        {Array.from({ length: n }, (_, i) => (
          <span key={i} className="reward-stack" style={{ transform: `translate(${(i - (n - 1) / 2) * size * 0.24}px, ${(i % 2) * size * 0.06}px)` }}>
            <GemIcon size={size * (n === 1 ? 0.66 : 0.52)} />
          </span>
        ))}
      </span>
    );
  }
  if (r.kind === 'token')
    return (
      <span className="reward-art reward-token" style={{ width: size, height: size }} aria-hidden>
        <TokenIcon token={r.token} size={size * 0.78} />
      </span>
    );
  const cos = COSMETIC_DEF[r.item];
  if (cos) return <CosmeticArt d={cos} size={size} />;
  return <ItemArt kind={itemKind(r.item)} id={r.item} size={size} />;
}

// ─── Aperçu d'un vrai cosmétique (objets du pass à partir de la saison 2) ─────
/** Teintes d'aperçu par collection (base, accent) : celles de la planche des cosmétiques. */
const COL_TINT: Record<string, [number, number]> = { noel: [0xc8102e, 0xf4f1ea] };
/** Teintes propres à quelques objets (pain d'épices brun, sapin vert…), sinon celles de la collection. */
const ITEM_TINT: Record<string, [number, number]> = {
  'mat:paindepices': [0xa0612f, 0xf4f1ea],
  'pat:sucredorge': [0xf4f1ea, 0xc8102e],
  'pat:emballage': [0x1f7a4d, 0xffd166],
  'dec:sapin': [0x22262e, 0x3cb371],
  'dec:houx': [0x22262e, 0x2f9e44],
  'dec:renne': [0x22262e, 0xc08a5a],
  'dec:etoilenoel': [0x22262e, 0xffd166],
};
const tintOf = (d: CosmeticDef): [number, number] => ITEM_TINT[d.id] ?? COL_TINT[d.collection] ?? [DEFAULT_LOOK.parts.hull.color, 0xeef3f8];

/** Pièce peinte qui montre l'objet seul (matière, motif ou autocollant). */
function samplePaint(d: CosmeticDef): PartPaint {
  const [base, accent] = tintOf(d);
  if (d.slot === 'mat') return { color: base, mat: d.id, pat: null, patColor: accent, dec: null, decColor: accent };
  if (d.slot === 'pat') return { color: base, mat: 'mat:satin', pat: d.id, patColor: accent, dec: null, decColor: accent };
  return { color: d.slot === 'dec' ? 0x3b4252 : base, mat: 'mat:satin', pat: null, patColor: accent, dec: d.slot === 'dec' ? d.id : null, decColor: accent };
}

/** Tank de base peint avec l'objet : matière sur toutes les pièces, motif sur caisse, tourelle et chenilles, autocollant sur les chenilles. */
function sampleLook(d: CosmeticDef): TankLook {
  const [base, accent] = tintOf(d);
  const p = DEFAULT_LOOK.parts;
  const parts = { ...p };
  if (d.slot === 'mat') {
    const m = (color: number): PartPaint => ({ ...p.hull, color, mat: d.id });
    parts.hull = m(base);
    parts.turret = m(base);
    parts.barrels = m(base);
    parts.treads = { ...p.treads, color: 0x2b3038, mat: d.id };
  } else if (d.slot === 'pat') {
    const m = (color: number): PartPaint => ({ ...p.hull, color, mat: 'mat:satin', pat: d.id, patColor: accent });
    parts.hull = m(base);
    parts.turret = m(base);
    parts.barrels = { ...p.barrels, color: base, mat: 'mat:satin' };
    parts.treads = m(0x2b3038);
  } else if (d.slot === 'dec') {
    parts.hull = { ...p.hull, color: 0x3b4252, mat: 'mat:satin' };
    parts.turret = { ...p.turret, color: 0x3b4252, mat: 'mat:satin' };
    parts.barrels = { ...p.barrels, color: 0x2e3440 };
    parts.treads = { ...p.treads, color: 0x22262e, dec: d.id, decColor: accent };
  }
  return { ...DEFAULT_LOOK, parts };
}

const SLOT_GLYPH: Partial<Record<CosmeticSlot, ItemKind>> = { aura: 'aura', trail: 'trail', shot: 'shotfx', kill: 'killfx', death: 'deathfx' };

/**
 * Aperçu d'un cosmétique, avec les dessins du jeu : matière sur un petit tank ; motif et autocollant en échantillon
 * (sur un tank à partir de 80 px, où il se lit) ; effets en pictogramme de leur emplacement. Liseré à la couleur de rareté.
 */
export function CosmeticArt({ d, size = 48 }: { d: CosmeticDef; size?: number }) {
  const rarity = css(RARITY_COLOR[d.rarity]);
  const style = { width: size, height: size, '--rarity': rarity } as CSSProperties;
  const glyph = SLOT_GLYPH[d.slot];
  if (glyph)
    return (
      <span className={`reward-art reward-cos reward-cos-fx rarity-${d.rarity}`} style={style} aria-hidden>
        <svg viewBox="0 0 48 48" width={size} height={size} focusable="false">
          <ItemGlyph kind={glyph} tint={rarity} seed={hash(d.id)} />
        </svg>
      </span>
    );
  if (d.slot === 'mat' || size >= 80)
    return (
      <span className={`reward-art reward-cos rarity-${d.rarity}`} style={style} aria-hidden>
        <TankImage cls={getClass('BASE')} look={{ painted: sampleLook(d) }} size={Math.round(size * 0.92)} />
        {d.slot !== 'mat' && (
          // Grand aperçu : le motif ou l'autocollant est aussi montré à plat, en médaillon.
          <span className="reward-cos-inset">
            <SwatchImage paint={samplePaint(d)} size={Math.round(size * 0.38)} />
          </span>
        )}
      </span>
    );
  return (
    <span className={`reward-art reward-cos reward-cos-swatch rarity-${d.rarity}`} style={style} aria-hidden>
      <SwatchImage paint={samplePaint(d)} size={Math.round(size * 0.78)} />
    </span>
  );
}

/** Pictogramme d'un objet sans palette (emblème, bannière, effet, traînée, cadre) ou plan d'une collection. */
export function ItemArt({ kind, id, size = 48 }: { kind: ItemKind; id: string; size?: number }) {
  const h = hash(id);
  if (kind === 'collection')
    return (
      <span className="reward-art reward-collection" style={{ width: size, height: size }} aria-hidden>
        <TankImage cls={getClass(BLUEPRINT_CLASSES[h % BLUEPRINT_CLASSES.length])} look={{ palette: BLUEPRINT }} size={Math.round(size * 0.92)} />
      </span>
    );
  return (
    <span className="reward-art" style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 48 48" width={size} height={size} focusable="false">
        <ItemGlyph kind={kind} tint={TINTS[h % TINTS.length]} seed={h} />
      </svg>
    </span>
  );
}
